import { Injectable, OnDestroy, Renderer2 } from '@angular/core';
import { MatTooltip } from '@angular/material';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { SubscriptionPlan } from 'src/API';
import { LayoutsService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { deepMerge } from 'src/app/shared/util/helper.util';
import {
  AccessConfig,
  liteSubscriptionPlanSchema,
  proSubscriptionPlanSchema,
  SubscriptionPlanSchema
} from '../schemas/subscription-plan.schema';
import { ClinicSetup } from './../../../API';
import { AppStateService } from './app-state.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionPlanAccessService implements OnDestroy {
  public subscriptionPlan: SubscriptionPlan;
  public subscriptionPlanSchema: SubscriptionPlanSchema;
  public subscriptionPlanAddOns: SubscriptionPlanSchema;

  public flattenedSubscriptionPlanSchema = this.flattenSchema(liteSubscriptionPlanSchema);
  public flattenedSubscriptionPlanSchema$: Observable<any>;

  constructor(private appStateService: AppStateService, private layoutsService: LayoutsService) {
    this.appStateService.clinic$.pipe(take(1)).subscribe(clinic => {
      this.setSubscriptionPlan(clinic.clinicSetup);
    });
  }

  ngOnDestroy() {}

  isAccessConfig(obj: any): obj is AccessConfig {
    return obj && typeof obj === 'object' && 'accessState' in obj;
  }

  flattenSchema(schema: Record<string, any>, prefix = ''): Record<string, AccessConfig> {
    let result: Record<string, any> = {};

    Object.entries(schema).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (this.isAccessConfig(value) || typeof value === 'number') {
        // Keep AccessConfig or numbers as is
        result[newKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        // Recurse into nested objects
        result = { ...result, ...this.flattenSchema(value, newKey) };
      }
    });

    return result;
  }

  public setSubscriptionPlan(clinicSetup: ClinicSetup) {
    this.subscriptionPlanAddOns = JSON.parse(clinicSetup.subscriptionPlanAddOns || '{}');

    // DEFAULT IS LITE, Cut off for full plan by default is Jan 16, 2026
    this.subscriptionPlan =
      clinicSetup.subscriptionPlan ||
      (new Date('Jan 16, 2026') > new Date(clinicSetup.createdAt) && SubscriptionPlan.full) ||
      SubscriptionPlan.lite;

    this.subscriptionPlanSchema = liteSubscriptionPlanSchema;

    switch (this.subscriptionPlan) {
      case SubscriptionPlan.pro: {
        this.subscriptionPlanSchema = proSubscriptionPlanSchema;
        break;
      }
      case SubscriptionPlan.full: {
        this.subscriptionPlanSchema = {};
        break;
      }
      case SubscriptionPlan.lite: {
        this.subscriptionPlanSchema = liteSubscriptionPlanSchema;
        break;
      }
    }

    this.subscriptionPlanSchema = deepMerge(
      this.subscriptionPlanSchema,
      this.subscriptionPlanAddOns
    );

    this.flattenedSubscriptionPlanSchema = this.flattenSchema(this.subscriptionPlanSchema);

    this.layoutsService.setLayouts(clinicSetup.layouts, this.subscriptionPlanSchema);
  }

  public applyLockedState(
    key: string,
    el: HTMLElement,
    renderer: Renderer2,
    document,
    badgeText: string,
    tooltip: MatTooltip,
    tooltipMessage: string
  ) {
    this.applyDisabledStyles(el, renderer);
    this.addLockedBadge(key, el, renderer, document, badgeText);
    tooltip.message = tooltipMessage;
    tooltip.position = 'after';
    this.addInteractionBlocker(el, renderer);
  }

  public addLockedBadge(key: string, el: HTMLElement, renderer: Renderer2, document, text: string) {
    renderer.addClass(el, 'pro-locked');

    if (!document.getElementById(`'pro-locked-style-'${key}`)) {
      const style = renderer.createElement('style');
      style.id = `'pro-locked-style-'${key}`;
      style.textContent = `
        .pro-locked::after {
          content: '${text}';
          color: #0224d3;
          position: absolute;
          top: 2px;
          right: 2px;
          font-size: 9.5px;
          padding: 2px 5px;
          border-radius: 8px;
          border: 2px solid #58eaff;
          font-weight: 500;
        }
      `;
      renderer.appendChild(document.head, style);
    }
  }

  public applyDisabledStyles(el: HTMLElement, renderer: Renderer2) {
    renderer.setStyle(el, 'opacity', '0.6');
    renderer.setStyle(el, 'filter', 'grayscale(0.4)');
  }

  public clearDisabledStyles(el: HTMLElement, renderer: Renderer2) {
    renderer.removeStyle(el, 'opacity');
    renderer.removeStyle(el, 'filter');
  }

  /**
   * Blocks clicks but lets hover event go through
   */
  public addInteractionBlocker(el: HTMLElement, renderer: Renderer2) {
    const blocker = renderer.createElement('div');
    renderer.addClass(blocker, 'interaction-blocker');

    renderer.setStyle(blocker, 'position', 'absolute');
    renderer.setStyle(blocker, 'top', '0');
    renderer.setStyle(blocker, 'left', '0');
    renderer.setStyle(blocker, 'right', '0');
    renderer.setStyle(blocker, 'bottom', '0');
    renderer.setStyle(blocker, 'z-index', '999');
    renderer.setStyle(blocker, 'background', 'transparent');
    renderer.setStyle(blocker, 'pointer-events', 'auto'); // <-- blocks clicks
    renderer.setStyle(blocker, 'cursor', 'default');
    renderer.appendChild(el, blocker);
  }

  public removeInteractionBlocker(el: HTMLElement, renderer: Renderer2) {
    const blocker = el.querySelector('.interaction-blocker');
    if (blocker) {
      renderer.removeChild(el, blocker);
    }
  }
}
